import React, { useState, useMemo, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import styled from "styled-components";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentEditable from "react-contenteditable";
import { useDispatch } from "react-redux";
import { openModal, closeModal } from "../../../../redux/reducers/dialog";

// Internal Components
import TextView from "../../../TextView";
import WheelComponent from "../../../WheelComponent";
import WheelStatus from "./WheelStatus";
import AccessKeyEnterModal from "../../../AccessKeyEnterModal";
import Timer from "./Timer";
import WheelCardThreeDotMenu from "../../../WheelCardThreeDotMenu/WheelCardThreeDotMenu";

// Styles
import {
  CardContainer,
  CardHeaderContainer,
  CardImageContainer,
  CardWallpaperBackground,
} from "../styles";

// Utils
import { ASSETS } from "../../../../utils/config";
import {
  getLoggedInUser,
  isUserLoggedIn,
  transformEditorTextNoStyles,
} from "../../../../utils/helpers";
import { getWheelMenuItems } from "../../../../utils/wheelMenuHelpers";
import {
  useUnlockWheelV2,
  useToggleWheelActive,
  useDeleteWheelV2,
} from "../../../../services/mutations";

const DisabledOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  background-color: rgba(255, 251, 251, 0.4);
  border-radius: 4px;
`;

const StatusContainer = styled.div`
  position: relative;
  min-height: 40px;
  overflow: hidden;
`;

const WheelStatusWrapper = styled.div`
  opacity: ${(props) => (props.isHovered ? 0 : 1)};
  transition: opacity 0.1s ease;
`;

const ActionContainer = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${(props) => (props.isHovered ? 1 : 0)};
  transition: opacity 0.2s ease;
  border-radius: 0 0 8px 8px;
  box-sizing: border-box;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 5px;
  padding: 0;
  width: 100%;
  box-sizing: border-box;
`;

const StyledButton = styled(Button)`
  && {
    text-transform: none;
    flex: 1;
    font-family: "DM Sans";
    font-weight: 500;
    font-size: 13px;
    line-height: 20px;
    height: 36px;
    white-space: nowrap;

    .MuiCircularProgress-root {
      width: 18px !important;
      height: 18px !important;
      margin-right: 8px;
    }
  }
`;

const TitleContentWrapper = styled.div`
  p {
    margin: 0;
    padding: 0;
  }
  br:last-child {
    display: none;
  }
`;

const WheelCategories = {
  Created: "Created",
  Cloned: "Cloned",
  Unlocked: "Unlocked",
  Favourited: "Favourited",
};

const WheelCard = ({
  data = {},
  onWheelDelete,
  wheelCategory,
  customMenuItems = [],
  isInsideFolder = false,
  excludeMenuItems = [],
  onReactivate,
}) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const nav = useNavigate();
  const unlockWheel = useUnlockWheelV2();
  const toggleWheelActive = useToggleWheelActive();
  const deleteWheelFunc = useDeleteWheelV2();

  const wheelIdRef = useRef(uuidv4());
  const isOthersGallery = useMemo(() => id != getLoggedInUser()?.id, [id]);
  const isLoggedIn = useMemo(() => isUserLoggedIn(), []);
  const is_disabled = useMemo(() => !data?.is_active, [data?.is_active]);

  // Set different deletion times based on wheel ID instead of index
  // const expected_deletion_time = useMemo(() => {
  //   // Use the last character of the wheel ID to determine the timer
  //   // This ensures the timer assignment remains stable even when other wheels are deleted
  //   const lastChar = String(data?.id || '').slice(-1);
  //   const isEven = parseInt(lastChar, 16) % 2 === 0; // Use hex parsing since ID might contain letters
  //   return isEven ? "2025-04-12T14:48:00.000000Z" : "2025-04-14T12:50:00.000000Z";
  // }, [data?.id]);
  const expected_deletion_time = data?.expected_deletion_time;

  const [isHovered, setIsHovered] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);

  // Memoized values
  const wheelSegments = useMemo(
    () => (Array.isArray(data?.wheel_segments) ? data.wheel_segments : []),
    [data?.wheel_segments]
  );

  const wheelUpdatedData = useMemo(
    () => wheelSegments.map((obj) => ({ ...obj })),
    [wheelSegments]
  );

  const wheelUpdatedDataSorted = useMemo(
    () => [...wheelUpdatedData].sort((a, b) => a.position - b.position),
    [wheelUpdatedData]
  );

  const wallpaperSettings = data?.wallpaper_settings ?? {};
  const wallpaperHueColor =
    wallpaperSettings.wallpaperHueColor ??
    wallpaperSettings.wallpaper_hue_color;
  const wallpaperHuePercentage =
    wallpaperSettings.wallpaperHuePercentage ??
    wallpaperSettings.wallpaper_hue_percentage ??
    0;

  const transformedWheelName = useMemo(
    () => transformEditorTextNoStyles(data?.wheel_name),
    [data?.wheel_name]
  );

  const menuItems = useMemo(
    () =>
      getWheelMenuItems(
        isInsideFolder
          ? data?.is_cloned
            ? WheelCategories.Cloned
            : WheelCategories.Created
          : wheelCategory,
        data,
        isOthersGallery,
        isLoggedIn
      ),
    [isInsideFolder, data, wheelCategory, isOthersGallery, isLoggedIn]
  );

  // Handlers
  const handleOpenPreview = useCallback(() => {
    if (is_disabled) return;

    if (data?.is_locked && !data?.is_unlocked) {
      setOpenPasswordModal(true);
      return;
    }
    if (data?.version === 2) {
      nav(`/wheel/${data?.id}`);
    } else {
      nav(`/wheelView/${data?.id}`);
    }
  }, [
    is_disabled,
    data?.is_locked,
    data?.is_unlocked,
    data?.version,
    data?.id,
    nav,
  ]);

  const handleReactivate = useCallback(() => {
    setIsReactivating(true);
    toggleWheelActive.mutate(data?.id, {
      onSuccess: () => {
        toast.success("Wheel reactivated successfully");
        onReactivate?.(data?.id);
      },
      onError: () => {
        toast.error("Failed to reactivate wheel");
      },
      onSettled: () => {
        setIsReactivating(false);
      },
    });
  }, [data?.id, onReactivate, toggleWheelActive]);

  const handleDelete = useCallback(() => {
    dispatch(
      openModal({
        title: "Delete Wheel",
        subtitle: "Are you sure you want to delete this Wheel?",
        onCancel: () => dispatch(closeModal()),
        onConfirm: () => {
          setIsDeleting(true);
          deleteWheelFunc.mutate(data?.id, {
            onSuccess: () => {
              toast.success("Wheel deleted successfully");
              onWheelDelete?.(data?.id);
            },
            onError: () => {
              toast.error("Failed to delete wheel");
            },
            onSettled: () => {
              setIsDeleting(false);
              dispatch(closeModal());
            },
          });
        },
      })
    );
  }, [data?.id, onWheelDelete, deleteWheelFunc, dispatch]);

  const handleUnlock = useCallback(
    (access_code) => {
      if (!access_code) {
        toast.error("Access code cannot be empty");
        return;
      }

      unlockWheel.mutate(
        { wheelId: data?.id, access_code, lock_type: 2 },
        {
          onSuccess: () => {
            toast.success("Wheel Unlocked");
            nav(
              data?.version === 2
                ? `/wheel/${data?.id}`
                : `/wheelView/${data?.id}`
            );
            setOpenPasswordModal(false);
          },
          onError: () => {
            toast.error("Incorrect Access Code");
          },
        }
      );
    },
    [data?.id, data?.version, unlockWheel, nav]
  );

  const getWheelBackground = useCallback((wallpaperSettings = {}) => {
    const { bg_wallpaper, current_bg, segment_bg_color } = wallpaperSettings;
    return current_bg === 2 || current_bg === 3
      ? bg_wallpaper
      : segment_bg_color;
  }, []);

  // Wheel props
  const wheelProps = useMemo(
    () => ({
      userData: {
        id: wheelIdRef.current,
        url: data?.wheel_image || ASSETS.userPlaceholder,
      },
      maxSelectedArcs: 0,
      arcThickness: -10,
      chartData: wheelUpdatedDataSorted,
      margin: 25,
      setChartData: () => {},
      width: "20vh",
      containerDivStyle: {
        width: "20vh",
        height: "20vh",
      },
    }),
    [data?.wheel_image, wheelUpdatedDataSorted]
  );

  return (
    <>
      <AccessKeyEnterModal
        onSubmit={handleUnlock}
        handleClose={() => setOpenPasswordModal(false)}
        isOpen={openPasswordModal}
      />

      <CardContainer
        style={{ position: "relative" }}
        onMouseEnter={() =>
          is_disabled && !isOthersGallery && setIsHovered(true)
        }
        onMouseLeave={() =>
          is_disabled && !isOthersGallery && setIsHovered(false)
        }
      >
        <CardHeaderContainer>
          <TextView
            variant="largeText"
            style={{
              cursor: is_disabled ? "default" : "pointer",
              WebkitLineClamp: 2,
              fontWeight: 600,
            }}
            onClick={is_disabled ? undefined : handleOpenPreview}
          >
            <TitleContentWrapper>
              <ContentEditable html={transformedWheelName} disabled />
            </TitleContentWrapper>
          </TextView>
          {!is_disabled && (
            <WheelCardThreeDotMenu
              onWheelDelete={onWheelDelete}
              data={data}
              include={menuItems}
              customMenuItems={customMenuItems}
              exclude={excludeMenuItems}
            />
          )}
        </CardHeaderContainer>

        <CardImageContainer
          onClick={is_disabled ? undefined : handleOpenPreview}
          style={{
            cursor: is_disabled ? "default" : "pointer",
          }}
        >
          <CardWallpaperBackground
            bg={
              data?.version === 1
                ? data?.wallpaper
                : getWheelBackground(data?.wallpaper_settings)
            }
            wallpaperHueColor={wallpaperHueColor}
            wallpaperHuePercentage={wallpaperHuePercentage}
          />
          <div style={{ position: "relative", zIndex: 1 }}>
            <WheelComponent {...wheelProps} />
          </div>

          {is_disabled && (
            <DisabledOverlay>
              <Timer
                expected_deletion_time={expected_deletion_time}
                onWheelDelete={onWheelDelete}
                wheelId={data?.id}
              />
            </DisabledOverlay>
          )}
        </CardImageContainer>

        <StatusContainer>
          <WheelStatusWrapper
            isHovered={is_disabled && isHovered && !isOthersGallery}
          >
            <WheelStatus isOthersGallery={isOthersGallery} data={data} />
          </WheelStatusWrapper>
          {is_disabled && !isOthersGallery && (
            <ActionContainer isHovered={isHovered}>
              <ButtonsContainer>
                <StyledButton
                  variant="outlined"
                  startIcon={
                    !isReactivating && <CheckCircleIcon sx={{ fontSize: 14 }} />
                  }
                  onClick={handleReactivate}
                  disabled={isReactivating || isDeleting}
                  sx={{
                    borderColor: "#8126DB",
                    color: "#8F38EF",
                    "&:hover": {
                      borderColor: "#8126DB",
                      backgroundColor: "rgba(129, 38, 219, 0.04)",
                    },
                  }}
                >
                  {isReactivating ? (
                    <CircularProgress size={18} sx={{ color: "#8F38EF" }} />
                  ) : (
                    "Reactivate"
                  )}
                </StyledButton>
                <StyledButton
                  variant="outlined"
                  startIcon={
                    !isDeleting && <DeleteIcon sx={{ fontSize: 14 }} />
                  }
                  onClick={handleDelete}
                  disabled={isReactivating || isDeleting}
                  sx={{
                    borderColor: "#D64646",
                    color: "#FC5555",
                    "&:hover": {
                      borderColor: "#D64646",
                      backgroundColor: "rgba(214, 70, 70, 0.04)",
                    },
                  }}
                >
                  {isDeleting ? (
                    <CircularProgress size={18} sx={{ color: "#FC5555" }} />
                  ) : (
                    "Delete"
                  )}
                </StyledButton>
              </ButtonsContainer>
            </ActionContainer>
          )}
        </StatusContainer>
      </CardContainer>
    </>
  );
};

export default React.memo(WheelCard);
